import { useState } from "react";

import { PanelTitleBar } from "../app/CollapsiblePanel";
import { useBackend } from "../app/BackendProvider";
import { useNetworkId } from "../config/ConfigProvider";
import type { Journey } from "../contract/types";
import { JourneyDetailPanel } from "./JourneyDetailPanel";
import { usePolledResource } from "./hooks";
import { RegulationTools } from "./RegulationTools";

/**
 * Colonne 3 du cockpit — « Outils ». Recompose la troisième colonne des
 * maquettes, vidée par l'abandon du lot d'action (analyse-maquettes §7) :
 * détail de la course, puis les outils d'aide à la décision (`RegulationTools`),
 * à l'échelle du réseau ou du véhicule sélectionné. Tout est consultatif —
 * aucune écriture.
 */
export function ToolsPanel({
  selectedJourneyId,
  onSelectJourney,
  onCollapse,
}: {
  selectedJourneyId: string | null;
  onSelectJourney: (id: string) => void;
  onCollapse: () => void;
}) {
  // §I : les OUTILS sont repliables/repliés par défaut ; le détail de course
  // reste toujours visible (contenu principal de la colonne).
  const [anticipationCount, setAnticipationCount] = useState(0);
  const journey = useSelectedJourney(selectedJourneyId);
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <PanelTitleBar title="Outils" side="left" onCollapse={onCollapse} />
      <div className="mae-scroll-y" style={{ flex: 1, minHeight: 0 }}>
        <section style={{ borderBottom: "1px solid var(--sentinelle-rule)", padding: "12px 14px" }}>
          <JourneyDetailPanel journeyId={selectedJourneyId} />
        </section>
        <RegulationTools
          journey={journey}
          storagePrefix="sentinelle.cockpit.tools"
          onSelectJourney={onSelectJourney}
          anticipationCount={anticipationCount}
          onAnticipationCount={setAnticipationCount}
        />
      </div>
    </div>
  );
}

/**
 * La course sélectionnée, en entier (le cockpit ne garde que son id). Rafraîchie
 * régulièrement : le rapprochement de sortie (`runId`) s'établit en cours de route.
 */
function useSelectedJourney(id: string | null): Journey | null {
  const backend = useBackend();
  const networkId = useNetworkId();
  const { data } = usePolledResource(
    (signal) => (id ? backend.api.getJourney(networkId, id, signal) : Promise.resolve(null)),
    [backend, networkId, id],
    20_000,
  );
  // `data` peut encore être celui de la sélection précédente pendant le rechargement.
  return id && data && data.id === id ? data : null;
}
